import { useContext } from 'react';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export namespace OnlyFansVaultMediaReleaseForm {
    export interface Item {
        id: number;
        name: string;
        partnerSource: PartnerSource;
        type: ReleaseFormType;
        user: User;
    }

    export enum PartnerSource {
        Document = 'document',
        Tag = 'tag',
    }

    export enum ReleaseFormType {
        Document = 'document',
        User = 'user',
        Link = 'link',
    }

    export interface User {
        view: View;
        id: number;
        name: string;
        username: string;
        isVerified: boolean;
        avatar: string;
        avatarThumbs: AvatarThumbs;
        ivStatus: IvStatus;
        isFromGuest: boolean;
    }

    export interface AvatarThumbs {
        c50: string;
        c144: string;
    }

    export enum IvStatus {
        Approved = 'approved',
    }

    export enum View {
        Tu = 'tu',
    }
}

/**
 * A media's release forms split into the two buckets the MediaHolder tags with:
 * - taggedCreators: tagged users (OnlyFans rfTag)
 * - taggedReleaseForms: release/consent forms (OnlyFans rfPartner)
 */
export interface VaultMediaTaggedItems {
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
}

// type 'user' + partnerSource 'tag' is a tagged creator; link/document forms are release (consent) forms.
const isTaggedCreator = (form: OnlyFansVaultMediaReleaseForm.Item): boolean =>
    form.type === OnlyFansVaultMediaReleaseForm.ReleaseFormType.User &&
    form.partnerSource === OnlyFansVaultMediaReleaseForm.PartnerSource.Tag;
const isReleaseForm = (form: OnlyFansVaultMediaReleaseForm.Item): boolean =>
    form.type === OnlyFansVaultMediaReleaseForm.ReleaseFormType.Link ||
    form.type === OnlyFansVaultMediaReleaseForm.ReleaseFormType.Document;

const useSubscriberVaultMediaReleaseForms = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    // Fetch the raw release forms attached to a single Vault media item.
    const fetchVaultMediaReleaseForms = async (mediaId: number): Promise<OnlyFansVaultMediaReleaseForm.Item[]> => {
        if (!subscriber?._id || !userContext.jwtToken || !settingsContext.apiKey) {
            return [];
        }

        const query: string = `${settingsContext.routes.onlyFans.base}${subscriber._id}/vaultMediaFull/${mediaId}/releaseForms`;

        return axios
            .get(query)
            .then(response => (response.data as OnlyFansVaultMediaReleaseForm.Item[]) ?? [])
            .catch(() => {
                return [];
            });
    };

    /**
     * Fetch a media's release forms already split into tagged creators and release (consent) forms,
     * shaped as { id, name } so they can be merged straight into the MediaHolder's tagging state.
     */
    const getVaultMediaTaggedItems = async (mediaId: number): Promise<VaultMediaTaggedItems> => {
        const forms = await fetchVaultMediaReleaseForms(mediaId);

        return {
            taggedCreators: forms.filter(isTaggedCreator).map(form => ({ id: form.id, name: form.user?.name ?? form.name })),
            taggedReleaseForms: forms.filter(isReleaseForm).map(form => ({ id: form.id, name: form.name })),
        };
    };

    /**
     * Replace a media's tagged creators and release (consent) forms with the given sets.
     * Resolves true on success, false on any failure (the caller decides how to report it).
     */
    const setVaultMediaTags = async (mediaId: number, taggedCreatorIds: number[], taggedReleaseFormIds: number[]): Promise<boolean> => {
        if (!subscriber?._id || !userContext.jwtToken || !settingsContext.apiKey) {
            return false;
        }

        const query: string = `${settingsContext.routes.onlyFans.base}${subscriber._id}/vaultMediaFull/${mediaId}/tags`;

        return axios
            .put(query, { taggedCreatorIds, taggedReleaseFormIds })
            .then(response => response.status === 200 && !!response.data?.success)
            .catch(error => {
                console.error(error);

                return false;
            });
    };

    return { fetchVaultMediaReleaseForms, getVaultMediaTaggedItems, setVaultMediaTags };
};

export default useSubscriberVaultMediaReleaseForms;
